import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { PlusIconThin } from '../../../components/svgs/icons/PlusIconThin';
import { aceGray } from '../../../styles/colors';
import { ImageAttachment, ImageAttachmentManager } from '../ImageAttachmentManager';
import { s } from './styles';

interface ChatInputProps {
	placeholder?: string;
	onSubmit: (prompt: string, images?: ImageAttachment[]) => void;
	streamingInProgress: boolean;
	supportsImages?: boolean;
	maxImages?: number;
	initialImages?: ImageAttachment[];
}

export const ChatInput = ({
	placeholder = 'Ask me anything...',
	onSubmit,
	streamingInProgress = false,
	supportsImages = false,
	maxImages = 2,
	initialImages = [],
}: ChatInputProps) => {
	const textAreaRef = React.useRef<HTMLTextAreaElement>(null);
	const fileInputRef = React.useRef<HTMLInputElement>(null);
	const [images, setImages] = React.useState<ImageAttachment[]>(initialImages);

	// Update images when initialImages changes (e.g., when reopening dialog)
	React.useEffect(() => {
		setImages(initialImages);
	}, [initialImages]);

	// Auto-resize textarea
	React.useEffect(() => {
		const adjustHeight = () => {
			if (textAreaRef.current) {
				textAreaRef.current.style.height = 'auto';
				textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight + 2}px`;
			}
		};

		const textarea = textAreaRef.current;
		textarea?.addEventListener('input', adjustHeight);

		return () => {
			textarea?.removeEventListener('input', adjustHeight);
		};
	}, []);

	const handlePromptKeyPress = (ev: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (ev.key === 'Enter' && !ev.shiftKey && !ev.nativeEvent.isComposing) {
			ev.preventDefault();
			handleSubmit();
		}
	};

	const handleSubmit = () => {
		const prompt = textAreaRef.current?.value.trim();

		if (!prompt && images.length === 0) {
			return;
		}

		if (streamingInProgress) {
			toast.error('Please wait for the current response to finish.');
			return;
		}

		// For image dialogs, require at least one image
		if (supportsImages && images.length === 0 && !prompt) {
			toast.error('Please add at least one image or enter a prompt.');
			return;
		}

		onSubmit(prompt || '', images);

		// Clear input and images
		if (textAreaRef.current) {
			textAreaRef.current.value = '';
			textAreaRef.current.style.height = 'auto';
		}
		setImages([]);
	};

	const handleAddImageClick = () => {
		if (!streamingInProgress) {
			fileInputRef.current?.click();
		}
	};

	return (
		<div className={css(s.container)}>
			{supportsImages && (
				<div className={css(s.imageManagerContainer)}>
					<ImageAttachmentManager
						images={images}
						onImagesChange={setImages}
						maxImages={maxImages}
						disabled={streamingInProgress}
						fileInputRef={fileInputRef}
					/>
				</div>
			)}

			<div className={css(s.inputWrapper)}>
				{supportsImages && images.length < maxImages && (
					<button
						type='button'
						className={css(s.addImageButton)}
						onClick={handleAddImageClick}
						disabled={streamingInProgress}
						aria-label='Add image'
						title='Add image'
					>
						<PlusIconThin
							height={16}
							width={16}
							fillColor={streamingInProgress ? aceGray : aceGray}
							className={css(s.addImageIcon)}
						/>
					</button>
				)}
				<textarea
					ref={textAreaRef}
					placeholder={placeholder}
					onKeyPress={handlePromptKeyPress}
					className={css(s.promptInput)}
					disabled={streamingInProgress}
					data-slot='input'
				/>
			</div>
		</div>
	);
};
